import * as React from 'react';
import styled from 'styled-components';
import { IHuePickerProps } from './spectrum-picker-props';
import { useDrag, IConstrainedDragInfo } from '../../../hooks';
import { roundDecimalPoints } from '../../../utils';
import { selectThemeSemantics } from '../../../styling';
import { knobStyles, pickerSurfaceStyles } from './styles';

const HueSpectrum = styled.div.attrs((props: Pick<IHuePickerProps, 'dimensionSize'>) => props)`
    box-sizing: border-box;
    position: relative;
    display: inline-block;
    background: linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%);
    cursor: pointer;
    width: ${props => `${props.dimensionSize}px`};
    height: 24px;
    margin-top: ${selectThemeSemantics(({ gap }) => gap.m)};
    ${pickerSurfaceStyles}
`;
type HueDragProps = Pick<IHuePickerProps, 'pointerSize'> & { left: number };
const HueDrag = styled.div.attrs(({ left }: HueDragProps) => ({
    style: {
        left: `${left}%`
    }
})) <HueDragProps>`
    position: absolute;
    transform: translate(-50%, -50%);
    user-select: none;
    top: 50%;
    width: ${props => `${props.pointerSize}px`};
    height: ${props => `${props.pointerSize}px`};
    ${knobStyles}
`;

function HuePickerBase(props: IHuePickerProps) {
    const surfaceRef = React.useRef<HTMLDivElement>(null);
    const targetRef = React.useRef<HTMLDivElement>(null);
    const hueValue = React.useRef(props.value || 0);

    const headDragX = props.value;

    const handleDragging = React.useCallback((currentDragInfo: IConstrainedDragInfo) => {
        const oldHueValue = hueValue.current;
        const currentHueValue = roundDecimalPoints((currentDragInfo.left / 100) * 359);
        setCurrentDragInfo(currentDragInfo);
        if (oldHueValue === currentHueValue) {
            return;
        }

        hueValue.current = currentHueValue;

        if (props.onValueChange === undefined) {
            return;
        }

        props.onValueChange(currentHueValue);
    }, [props]);

    const [initialDragInfo, setDragPosition] = useDrag({
        target: targetRef,
        constraintsTo: surfaceRef,
        headDragX,
        headDragY: 0,
        dragBufferX: (props.pointerSize! / 2),
        unit: '%',
        onDragging: handleDragging
    });
    const [dragInfo, setCurrentDragInfo] = React.useState<IConstrainedDragInfo>(initialDragInfo);

    React.useEffect(() => {
        const position = {
            left: (props.value / 359) * 100, top: 0
        };

        setDragPosition(position.left, 0);
        setCurrentDragInfo(position);
    }, [props.value, props.dimensionSize, setDragPosition]);

    return (
        <HueSpectrum ref={surfaceRef} dimensionSize={props.dimensionSize} className={props.className}>
            <HueDrag ref={targetRef} pointerSize={props.pointerSize} left={dragInfo.left} />
        </HueSpectrum>
    );
}

HuePickerBase.defaultProps = {
    dimensionSize: 220,
    pointerSize: 24
};

export const HuePicker = React.memo(HuePickerBase);
